import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import confetti from "canvas-confetti";
import { toast } from "sonner";
import { api, sharedGet } from "@/lib/http";
import { Moon, Footprints, Droplet, Flame, Dumbbell, Target } from "lucide-react";

/**
 * A compact 6-stat "at a glance" card the client sees at the top of the
 * dashboard. Every stat is a single hue on white, with a big tabular
 * value that count-ups from 0, a mini progress arc / bar, and a tiny
 * unit label. Data flows in from the parent so we don't fetch anything
 * ourselves.
 */
/**
 * Fire the "all 3 daily missions complete" celebration: a two-burst confetti
 * cannon anchored on the GOALS cell, a celebratory haptic pattern on
 * mobile, and a persistent toast reminding the client of the SP bonus
 * they just earned. Everything is best-effort — if `navigator.vibrate`
 * isn't supported or the cell ref is null we still fire the confetti
 * from a sensible default position so the moment never feels flat.
 */
function _celebrateMissionsComplete(cellRef, bonusSp, t) {
  // Celebratory haptic — a longer, richer pattern than the tiny 6ms tick
  // used on plain taps, so the phone actually "cheers" with the user.
  try { navigator.vibrate?.([40, 60, 40, 60, 100]); } catch {}

  // Anchor the burst on the GOALS cell so particles feel connected to
  // the counter the user just watched hit 3/3.
  const rect = cellRef?.current?.getBoundingClientRect?.();
  const origin = rect && rect.width
    ? {
        x: (rect.left + rect.width / 2) / window.innerWidth,
        y: (rect.top + rect.height / 2) / window.innerHeight,
      }
    : { x: 0.5, y: 0.35 };

  // Solano brand palette — deep teal + amber gold + fresh green to mirror
  // the GOALS chip tone (#10B981) and the app's warm-up accent.
  const colors = ["#10B981", "#34D399", "#FC9700", "#FFD37E", "#2C5254", "#FFFFFF"];

  // First burst: wide spread from the cell so particles rain over the
  // whole "Your day" grid, not just the corner.
  confetti({
    particleCount: 140,
    spread: 90,
    origin,
    colors,
    scalar: 1.0,
    ticks: 220,
    startVelocity: 45,
  });
  // Second, slightly delayed follow-up cannon for that "fireworks
  // encore" feel — smaller but with more vertical lift.
  setTimeout(() => {
    confetti({
      particleCount: 70,
      spread: 60,
      origin: { x: origin.x, y: Math.max(0.15, origin.y - 0.05) },
      colors,
      scalar: 0.85,
      ticks: 180,
      startVelocity: 55,
    });
  }, 260);

  // Persistent toast so users who miss the confetti (glance away, bad
  // screen refresh) still see the reward moment. `t()` fallback keeps
  // the string translatable when the locale later gets the key.
  const title = t?.("dailySummary.missionsAllDone") || "Missioni completate!";
  const subtitle = (t?.("dailySummary.missionsAllDoneSub") || "+{n} SP bonus di giornata")
    .replace("{n}", String(bonusSp));
  try {
    toast.success(title, { description: subtitle, duration: 5000 });
  } catch {
    // sonner not mounted — silent, confetti + vibrate are enough.
  }
}


function useCountUp(target, duration = 900) {  const [value, setValue] = useState(0);
  useEffect(() => {
    if (target == null || target === 0) { setValue(0); return; }
    const start = performance.now();
    let raf;
    const step = (ts) => {
      const p = Math.min(1, (ts - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(target * eased);
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return value;
}

/**
 * A minimal semicircular "pill" progress that fills from left to right.
 * Kept CSS-only to stay fluid on low-end phones.
 */
const SOURCE_LABELS = { whoop: "WHOOP", apple: "Apple Health", health_connect: "Health Connect", vital: "Google Fit" };

function StatCell({ tone, icon: Icon, label, value, unit, pct, testId, to, onClick, hitTarget, danger, cellRef, fromHint, source, sourceManualLabel }) {
  const sourceLabel = source ? (SOURCE_LABELS[source] || sourceManualLabel) : null;
  // Clamp for animation, but let the caller pass raw values (>100% OK for display).
  const clampedPct = Math.max(0, Math.min(100, pct ?? 0));
  const [animPct, setAnimPct] = useState(0);
  useEffect(() => {
    const id = requestAnimationFrame(() => setAnimPct(clampedPct));
    return () => cancelAnimationFrame(id);
  }, [clampedPct]);
  // Danger (e.g. kcal over target or too far under target) turns the cell red.
  // Otherwise: auto-green when the daily goal is hit.
  const doneTone = "#10B981";
  const dangerTone = "#EF4444";
  const activeTone = danger ? dangerTone : hitTarget ? doneTone : tone;
  const containerCls =
    "group relative flex flex-col items-start gap-1.5 rounded-2xl bg-white dark:bg-[#1a1d1e] border border-slate-100 dark:border-slate-800 p-2.5 min-w-0 transition-transform duration-150 active:scale-[0.97] hover:border-slate-200 dark:hover:border-slate-700";
  const inner = (
    <>
      <div className="flex items-center gap-1.5 text-slate-500 dark:text-slate-400">
        <span
          className="h-6 w-6 rounded-full flex items-center justify-center transition-colors"
          style={{ background: `${activeTone}18`, color: activeTone }}
        >
          <Icon className="h-3 w-3" strokeWidth={2.5} />
        </span>
        <span className="text-[9px] uppercase tracking-wider font-semibold truncate">
          {label}
        </span>
        {hitTarget && (
          <span
            className="ml-auto h-3.5 w-3.5 rounded-full bg-emerald-500 text-white flex items-center justify-center animate-[popIn_0.4s_ease-out]"
            aria-label="goal reached"
            title="✓"
          >
            <svg className="h-2 w-2" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M2 6l2.5 2.5L10 3" /></svg>
          </span>
        )}
      </div>
      <div className="flex items-baseline gap-1 min-w-0">
        <span
          className="font-heading text-lg font-bold text-[#1A1D1E] dark:text-slate-100 tabular-nums leading-none truncate"
        >
          {value}
        </span>
        {unit && <span className="text-[10px] text-slate-400 font-medium">{unit}</span>}
      </div>
      {fromHint && (
        <span
          className="text-[9px] text-slate-400 font-medium leading-none -mt-0.5"
          data-testid={`${testId}-from-hint`}
        >
          {fromHint}
        </span>
      )}
      {!fromHint && sourceLabel && (
        <span
          className="text-[9px] text-slate-400 font-medium leading-none -mt-0.5 truncate max-w-full"
          data-testid={`${testId}-source`}
        >
          {sourceLabel}
        </span>
      )}
      {/* Track */}
      <div className="w-full h-1 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
        <div
          className="h-full rounded-full transition-[width,background-color] duration-1000 ease-out"
          style={{ width: `${animPct}%`, background: activeTone }}
        />
      </div>
    </>
  );
  return onClick ? (
    <button
      type="button"
      ref={cellRef}
      onClick={() => { try { navigator.vibrate?.(6); } catch {} onClick(); }}
      className={`${containerCls} text-left w-full`}
      data-testid={testId}
    >
      {inner}
    </button>
  ) : to ? (
    <Link
      ref={cellRef}
      to={to}
      className={containerCls}
      data-testid={testId}
      onClick={() => { try { navigator.vibrate?.(6); } catch {} }}
    >
      {inner}
    </Link>
  ) : (
    <div ref={cellRef} className={containerCls} data-testid={testId}>{inner}</div>
  );
}

export default function DailySummaryCard({
  t,
  sleepMinutes = 0,          // from vital_daily_metrics
  sleepTargetH = 8,
  sleepFromDate = null,       // ISO date if the value was backfilled from an earlier day (e.g. WHOOP overnight sync)
  steps = 0,
  stepsTarget = 8000,
  stepsFromDate = null,
  waterL = 0,
  waterTargetL = 2,
  kcalConsumed = 0,
  kcalTarget = 0,
  workoutMin = 0,
  workoutTarget = 30,
  workoutFromDate = null,
  sources = {},               // per-field provenance from /client/wearable/timeline
}) {
  // Goals — fetched from the gamification summary so the daily card stays in
  // sync with the rest of the app (single source of truth for missions).
  // Backend shape:
  //   { missions: { missions: [{completed, ...}, ...], all_completed: bool } }
  // Older mock/dev responses returned the array directly, so we accept both
  // shapes to stay resilient to future backend refactors.
  const [goals, setGoals] = useState({ done: 0, total: 0 });
  const [bonusSp, setBonusSp] = useState(0);
  // Anchor for the confetti burst so particles fly out from the GOALS cell
  // instead of the top-left corner of the viewport.
  const goalsCellRef = useRef(null);
  useEffect(() => {
    const load = () => sharedGet("/client/gamification/summary")
      .then(({ data }) => {
        const raw = data?.missions;
        const missions = Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.missions) ? raw.missions : [];
        const doneCount = missions.filter((m) => m.completed).length;
        const totalCount = missions.length;
        setGoals({ done: doneCount, total: totalCount });
        setBonusSp(Number(data?.daily_bonus_sp) || 50);

        // Fire the celebration exactly once per day. We celebrate on any
        // observation of "all missions complete" as long as we haven't
        // celebrated yet today — this covers the common flow where the
        // client completes the last mission on a different page (steps
        // logged, meal saved…) and then returns to the dashboard.
        if (totalCount > 0 && doneCount >= totalCount) {
          const dayKey = new Date().toISOString().slice(0, 10);
          const storageKey = `solano_daily_missions_celebrated_${dayKey}`;
          if (localStorage.getItem(storageKey) !== "1") {
            _celebrateMissionsComplete(goalsCellRef, Number(data?.daily_bonus_sp) || 50, t);
            localStorage.setItem(storageKey, "1");
          }
        }
      })
      .catch(() => {});
    load();
    // Re-poll every 60s so a mission completed elsewhere (e.g. water
    // logged from a different tab) still triggers the celebration.
    const iv = setInterval(load, 60_000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sleepH = sleepMinutes / 60;
  const sleepHDisplay = useCountUp(sleepH);
  const stepsDisplay = useCountUp(steps);
  // Water quick-add (tap tile → +250 / +500 / free ml)
  const [waterOpen, setWaterOpen] = useState(false);
  const [freeMl, setFreeMl] = useState("");
  const [waterExtraL, setWaterExtraL] = useState(0);
  const [waterBusy, setWaterBusy] = useState(false);
  // The optimistic extra only bridges the gap until the parent re-fetches;
  // once the waterL prop updates it already CONTAINS the added ml — keeping
  // the extra would double-count (tile showed 2.0 L vs. actual 1.5 L).
  useEffect(() => { setWaterExtraL(0); }, [waterL]);
  const effWaterL = waterL + waterExtraL;
  const addWater = async (ml) => {
    if (!ml || ml <= 0 || waterBusy) return;
    const date = (() => {
      // LOCAL date (not UTC) — must match the Dashboard's read date,
      // otherwise water logged between 00:00 and ~02:00 CET lands on
      // "yesterday" and vanishes on reload.
      const d = new Date();
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    })();
    setWaterBusy(true);
    try {
      const totalMl = Math.round(effWaterL * 1000) + Math.round(ml);
      await api.post("/client/water", { date, glasses: Math.round(totalMl / 250), ml: totalMl });
      // First crossing of the personal water target from the quick-add tile →
      // let the Dashboard show the mascot celebration (once-per-day guard there).
      if (effWaterL < waterTargetL && totalMl / 1000 >= waterTargetL) {
        try { window.dispatchEvent(new CustomEvent("solano:water-goal")); } catch { /* ignore */ }
      }
      setWaterExtraL((v) => v + ml / 1000);
      try { navigator.vibrate?.(10); } catch {}
      toast.success(`+${Math.round(ml)} ml 💧`);
      setWaterOpen(false);
      setFreeMl("");
    } catch {
      toast.error(t("common.error"));
    } finally {
      setWaterBusy(false);
    }
  };

  const waterDisplay = useCountUp(effWaterL);
  const kcalDisplay = useCountUp(kcalConsumed);
  const workoutDisplay = useCountUp(workoutMin);
  const goalsDisplay = useCountUp(goals.done);

  const fmtSleep = (h) => {
    if (h < 1) return "0h";
    const hh = Math.floor(h);
    const mm = Math.round((h - hh) * 60);
    return `${hh}h${mm ? ` ${mm}m` : ""}`;
  };

  // Turn an ISO date into a tiny relative hint used on the tile
  // (e.g. "gestern", "vor 2t") so the user immediately understands the
  // number came from an earlier WHOOP sync — not today.
  const fromHint = (iso) => {
    if (!iso) return null;
    try {
      const today = new Date(); today.setHours(0,0,0,0);
      const then  = new Date(iso); then.setHours(0,0,0,0);
      const dd = Math.round((today - then) / 86400000);
      if (dd <= 0) return null;
      if (dd === 1) return t("cliDash.yesterday") || "gestern";
      return t("common.daysAgoShort", { n: dd }) || `vor ${dd}t`;
    } catch { return null; }
  };

  const cells = [
    {
      tone: "#8B5CF6", icon: Moon,
      label: t("dailySummary.sleep"),
      value: sleepH > 0 ? fmtSleep(sleepHDisplay) : "—",
      unit: null,
      pct: sleepTargetH ? (sleepH / sleepTargetH) * 100 : 0,
      hitTarget: sleepTargetH ? sleepH >= sleepTargetH : false,
      testId: "daily-sleep",
      to: "/app/progressi",
      source: sources?.sleep_duration_minutes,
      fromHint: fromHint(sleepFromDate),
    },
    {
      tone: "#0EA5E9", icon: Footprints,
      label: t("dailySummary.steps"),
      value: Math.round(stepsDisplay).toLocaleString(),
      unit: null,
      pct: stepsTarget ? (steps / stepsTarget) * 100 : 0,
      hitTarget: stepsTarget ? steps >= stepsTarget : false,
      testId: "daily-steps",
      to: "/app/progressi",
      source: sources?.steps,
      fromHint: fromHint(stepsFromDate),
    },
    {
      tone: "#22C5D5", icon: Droplet,
      label: t("dailySummary.water"),
      value: waterDisplay.toFixed(1),
      unit: "L",
      pct: waterTargetL ? (effWaterL / waterTargetL) * 100 : 0,
      hitTarget: waterTargetL ? effWaterL >= waterTargetL : false,
      testId: "daily-water",
      onClick: () => setWaterOpen(true),
    },
    {
      tone: "#F59E0B", icon: Flame,
      label: t("dailySummary.kcal"),
      value: Math.round(kcalDisplay).toLocaleString(),
      unit: "kcal",
      pct: kcalTarget ? (kcalConsumed / kcalTarget) * 100 : 0,
      // Kcal color logic (per user request):
      //   consumed > target        → RED (over budget)
      //   consumed < 70% of target → RED (too far under)
      //   70% ≤ consumed ≤ target  → GREEN (safe zone)
      // The green checkmark only shows in the safe zone (≥70% and not over).
      hitTarget: kcalTarget ? (kcalConsumed >= kcalTarget * 0.7 && kcalConsumed <= kcalTarget) : false,
      danger: kcalTarget ? (kcalConsumed > kcalTarget || kcalConsumed < kcalTarget * 0.7) : false,
      testId: "daily-kcal",
      to: "/app/diario",
    },
    {
      tone: "#EF4444", icon: Dumbbell,
      label: t("dailySummary.workout"),
      value: Math.round(workoutDisplay),
      unit: "min",
      pct: workoutTarget ? (workoutMin / workoutTarget) * 100 : 0,
      hitTarget: workoutTarget ? workoutMin >= workoutTarget : false,
      testId: "daily-workout",
      to: "/app/allenamenti",
      fromHint: fromHint(workoutFromDate),
    },
    {
      tone: "#10B981", icon: Target,
      label: t("dailySummary.goals"),
      value: `${Math.round(goalsDisplay)}/${goals.total || "—"}`,
      unit: null,
      pct: goals.total ? (goals.done / goals.total) * 100 : 0,
      hitTarget: goals.total > 0 && goals.done >= goals.total,
      testId: "daily-goals",
      to: "/app/premi",
      cellRef: goalsCellRef,
    },
  ];

  return (
    <div className="mt-2" data-testid="daily-summary-card">
      <div className="flex items-center justify-between mb-2 px-0.5">
        <h3 className="font-heading text-sm font-bold text-[#1A1D1E] dark:text-slate-100">
          {t("dailySummary.title")}
        </h3>
        <span className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">
          {t("dailySummary.today")}
        </span>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {cells.map((c) => (
          <StatCell key={c.testId} {...c} sourceManualLabel={t("dailySummary.sourceManual")} />
        ))}
      </div>

      {waterOpen && (
        <div
          className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center bg-black/45 backdrop-blur-sm"
          onClick={() => setWaterOpen(false)}
          data-testid="water-quick-overlay"
        >
          <div
            className="w-full sm:w-[340px] rounded-t-3xl sm:rounded-3xl bg-white dark:bg-[#1a1d1e] border border-slate-100 dark:border-slate-800 p-5 pb-[max(env(safe-area-inset-bottom),1.25rem)] shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="water-quick-dialog"
          >
            <div className="flex items-center gap-2 mb-4">
              <span className="h-8 w-8 rounded-full bg-[#22C5D5]/15 text-[#22C5D5] flex items-center justify-center">
                <Droplet className="h-4 w-4" />
              </span>
              <div>
                <p className="font-heading font-bold text-sm text-[#1A1D1E] dark:text-slate-100">{t("dailySummary.waterQuickTitle")}</p>
                <p className="text-xs text-slate-500">{effWaterL.toFixed(1)} / {waterTargetL} L</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 mb-3">
              {[250, 500].map((ml) => (
                <button
                  key={ml}
                  onClick={() => addWater(ml)}
                  disabled={waterBusy}
                  data-testid={`water-add-${ml}`}
                  className="h-12 rounded-2xl bg-[#22C5D5]/12 text-[#0E7C88] dark:text-[#7DE3EC] font-bold text-base active:scale-95 transition disabled:opacity-50"
                >
                  +{ml} ml
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="number"
                inputMode="numeric"
                min="1"
                value={freeMl}
                onChange={(e) => setFreeMl(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") addWater(parseInt(freeMl, 10)); }}
                placeholder={t("dailySummary.waterCustomPlaceholder")}
                className="h-11 flex-1 min-w-0 rounded-xl border-0 bg-slate-100 dark:bg-slate-800 px-3 text-sm text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-[#22C5D5]"
                data-testid="water-free-input"
              />
              <button
                onClick={() => addWater(parseInt(freeMl, 10))}
                disabled={waterBusy || !parseInt(freeMl, 10)}
                data-testid="water-add-free"
                className="h-11 px-4 rounded-xl text-white font-semibold text-sm active:scale-95 transition disabled:opacity-50"
                style={{ background: "linear-gradient(135deg, #22C5D5, #0E7C88)" }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
